import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { getCopy } from "../../brand";
import { CheckCircle2, AlertTriangle, XCircle, Zap } from "lucide-react";

function getScoreColor(score) {
  if (score >= 80) return "text-[#14B8A6]";
  if (score >= 60) return "text-[#F59E0B]";
  return "text-red-500";
}

function getReadinessLabel(classification) {
  switch (classification) {
    case "field-ready": return { label: "Field Ready", textColor: "text-[#14B8A6]", bgLight: "bg-[#14B8A6]/10", icon: CheckCircle2 };
    case "needs-coaching": return { label: "Needs Coaching", textColor: "text-[#F59E0B]", bgLight: "bg-[#F59E0B]/10", icon: AlertTriangle };
    default: return { label: "Not Ready", textColor: "text-red-500", bgLight: "bg-red-50", icon: XCircle };
  }
}

function getScoreRingColor(score) {
  if (score >= 80) return "#14B8A6";
  if (score >= 60) return "#F59E0B";
  return "#EF4444";
}

export function ScoreHero({ evaluation, session }) {
  const readiness = getReadinessLabel(evaluation.readiness_classification);
  const ReadinessIcon = readiness.icon;
  const scoreColor = getScoreRingColor(evaluation.readiness_score);
  const circumference = 2 * Math.PI * 45;
  const scoreOffset = circumference - (evaluation.readiness_score / 100) * circumference;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 fade-up">
      {/* Large Readiness Score */}
      <Card className="premium-card lg:col-span-1" data-testid="readiness-score-card">
        <CardContent className="p-8 flex flex-col items-center justify-center text-center">
          <div className="relative w-36 h-36 mb-4">
            <svg className="w-36 h-36 transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="#E2E8F0" strokeWidth="6" />
              <circle 
                cx="50" cy="50" r="45" fill="none" 
                stroke={scoreColor} strokeWidth="6" 
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={scoreOffset}
                className="score-circle"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-4xl font-bold ${getScoreColor(evaluation.readiness_score)}`}>
                {evaluation.readiness_score}
              </span>
              <span className="text-xs text-slate-400 mt-0.5">out of 100</span>
            </div>
          </div>
          <Badge className={`${readiness.bgLight} ${readiness.textColor} px-4 py-1.5 text-sm font-semibold`}>
            <ReadinessIcon className="w-4 h-4 mr-1.5" />
            {readiness.label}
          </Badge>
          <p className="text-sm text-slate-500 mt-3">
            {getCopy.readinessMessage(evaluation.readiness_score)}
          </p>
        </CardContent>
      </Card>

      {/* Brutal Truth + Quick Stats */}
      <Card className="premium-card lg:col-span-2" data-testid="brutal-truth-card">
        <CardContent className="p-0">
          <div className="bg-[#0B1F3B] text-white p-6 rounded-t-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-[#F59E0B]" />
              <h3 className="font-bold text-[#F59E0B] text-sm uppercase tracking-wide">Brutal Truth</h3>
            </div>
            <p className="text-lg leading-relaxed font-medium">{evaluation.brutal_truth}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className={`text-3xl font-bold ${getScoreColor(evaluation.total_score)}`}>{evaluation.total_score}%</p>
                <p className="text-xs text-slate-500 mt-1">Overall Score</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-3xl font-bold text-[#0B1F3B]">{evaluation.behavioral_patterns?.talk_ratio_advisor || 0}%</p>
                <p className="text-xs text-slate-500 mt-1">Your Talk Ratio</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-3xl font-bold text-[#2563EB]">{session.messages?.length || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Total Turns</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
